// src/pages/HowToFind.js
import React, { useEffect } from 'react';
import Layout from '../components/Layout';
import translations from '../translations';
import { useMeta } from '../useMeta';

function HowToFind({ language = 'en' }) {
  const t = translations[language] || translations['en'];
  const prefix = language === 'en' ? '' : `/${language}`;

  useMeta({
    title: `${t.howToFind.title} | ERC Unlock`,
    description: language === 'en'
      ? 'Step-by-step guide to finding your 16-digit Toyota ERC serial number. Two methods: MAP button with small lamp, or picture quality adjustment mode. Includes headlights tip.'
      : undefined,
    canonical: `https://ercunlock.com${prefix}/how-to-find-erc-code`
  });

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ur' ? 'rtl' : 'ltr';
    document.title = `${t.howToFind.title} | ERC Unlock`;
  }, [language, t.howToFind.title]);

  // HowTo structured data
  const howToSchema = t.howToFind.methods ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": t.howToFind.title,
    "description": t.howToFind.description,
    "step": t.howToFind.methods.flatMap((method, mi) =>
      method.steps.map((step, si) => ({
        "@type": "HowToStep",
        "position": mi * 10 + si + 1,
        "name": `${method.title} — Step ${si + 1}`,
        "text": step
      }))
    )
  } : null;

  return (
    <Layout language={language}>
      {howToSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      )}
      <div className="content-card">
        <h1>{t.howToFind.title}</h1>
        <div className="prose">

          {t.howToFind.youtubeNote && (
            <div style={{
              background: '#fef9c3',
              border: '1px solid #fde047',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 24,
              fontSize: 15
            }}>
              <strong>💡</strong> {t.howToFind.youtubeNote}
            </div>
          )}

          <p>{t.howToFind.description}</p>

          {t.howToFind.methods && t.howToFind.methods.map((method, mi) => (
            <div key={mi} style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '18px 20px',
              marginBottom: 20
            }}>
              <h2 style={{ marginTop: 0, fontSize: 18 }}>{method.title}</h2>
              <p style={{ marginBottom: 12 }}>{method.intro}</p>
              <ol style={{ paddingLeft: 20, margin: 0 }}>
                {method.steps.map((step, si) => (
                  <li key={si} style={{ marginBottom: 8 }}>{step}</li>
                ))}
              </ol>
            </div>
          ))}

          {t.importGuide && (
            <>
              <hr />
              <h2>{t.importGuide.title}</h2>
              <p>{t.importGuide.intro}</p>
              {t.importGuide.sections.map((section, i) => (
                <div key={i}>
                  <h3>{section.heading}</h3>
                  <p>{section.text}</p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default HowToFind;
